import { PortalUserResolver, getFromIDB } from './persistence/indexeddb';
import { getGlobal, hasGlobal, setGlobal } from './persistence/global';
import { toNamespacedKey } from './utils/toNamespacedKey';
import stringify from 'fast-json-stable-stringify';
import quickFetch from 'quick-fetch';

const configKey = 'quickLoadConfig';

const defaultConfig = {
  quickLoadedRequests: new Map(),
};

const getConfig = () => {
  if (!hasGlobal(configKey)) {
    setGlobal(configKey, defaultConfig);
  }

  return getGlobal(configKey) as typeof defaultConfig;
};

export const resetConfig = () => {
  const config = getConfig();
  config.quickLoadedRequests = new Map();
};

export const hasQuickLoadRequest = ({
  namespace,
  entityName,
  cacheKey,
}: {
  namespace: string;
  entityName: string;
  cacheKey: string;
}) =>
  getConfig().quickLoadedRequests.has(
    toNamespacedKey(namespace, entityName, cacheKey)
  );

export const getQuickLoadRequest = ({
  namespace,
  entityName,
  cacheKey,
}: {
  namespace: string;
  entityName: string;
  cacheKey: string;
}) =>
  getConfig().quickLoadedRequests.get(
    toNamespacedKey(namespace, entityName, cacheKey)
  );

export const removeQuickLoadRequest = ({
  namespace,
  entityName,
  cacheKey,
}: {
  namespace: string;
  entityName: string;
  cacheKey: string;
}) =>
  getConfig().quickLoadedRequests.delete(
    toNamespacedKey(namespace, entityName, cacheKey)
  );

const defaultQuickLoadPortalUserResolver = () => {
  if (!quickFetch.getLoginVerifyRequest()) {
    return Promise.reject(Error('Cannot derive portal/userId from quickFetch'));
  }

  return new Promise<{ userId: number; portalId: number }>((resolve) => {
    quickFetch.afterAuth((auth: any) => {
      resolve({
        portalId: auth?.portal?.portal_id,
        userId: auth?.user?.user_id,
      });
    });
  });
};

export const makeQuickLoadRequest = ({
  namespace,
  entityName,
  cacheKey,
  resolvePortalAndUserId = defaultQuickLoadPortalUserResolver,
}: {
  namespace: string;
  entityName: string;
  cacheKey: string;
  resolvePortalAndUserId?: PortalUserResolver;
}) => {
  if (!hasQuickLoadRequest({ namespace, entityName, cacheKey })) {
    const key = toNamespacedKey(namespace, entityName, cacheKey);
    getConfig().quickLoadedRequests.set(
      key,
      getFromIDB({ key, resolvePortalAndUserId })
    );
  }

  getQuickLoadRequest({ namespace, entityName, cacheKey })?.catch(() => {
    // do nothing. This suppresses unhandled promise rejection errors that
    // would otherwise be thrown if the login-verify request isn't present.
    // The errors *will* be handled/retried when consuming code calls
    // withQuickLoad, but that doesn't happen soon enough for this rejection.
  });
};

export const defaultToCacheKey = <TArgs extends any[]>(...args: TArgs) =>
  stringify(args);

export const quickLoadPersistedPromise = (
  {
    namespace,
    entityName,
    toCacheKey = defaultToCacheKey,
  }: {
    namespace: string;
    entityName: string;
    toCacheKey?: (...args: any[]) => string;
  },
  ...fetcherArgs: any[]
) =>
  makeQuickLoadRequest({
    namespace,
    entityName,
    cacheKey: toCacheKey(fetcherArgs),
  });
